import { useState, useEffect, useMemo, useTransition } from 'react'
import { useInView } from 'react-intersection-observer'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { newsApi, curationApi } from '@/services/api'
import { Loader2, ArrowLeft, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getViewMode } from '@/lib/utils'
import { toast } from 'sonner'

// Infinite scroll feed for curation
function CurationFeed({ curationParam, curatorName }) {
  const [isPending, startTransition] = useTransition()
  const navigate = useNavigate()

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '200px',
  })

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['curationFeed', curationParam],
    queryFn: ({ pageParam = 1 }) => {
      return newsApi.getFeed(pageParam, null, curationParam)
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.data.next) return undefined
      try {
        const url = new URL(lastPage.data.next)
        const page = url.searchParams.get('page')
        return page ? parseInt(page, 10) : undefined
      } catch {
        return undefined
      }
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })

  // Memoize articles
  const articles = useMemo(() => {
    if (!data?.pages) return []
    return data.pages
      .flatMap(page => page?.data?.results || [])
      .filter(article => article != null)
  }, [data?.pages])

  // Load more when in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      startTransition(() => {
        fetchNextPage()
      })
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Loading state
  if (isLoading) {
    const skeletonHeights = ['min-h-[280px]', 'min-h-[320px]', 'min-h-[300px]', 'min-h-[290px]', 'min-h-[310px]']
    return (
      <div className="flex flex-col gap-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className={skeletonHeights[i]}>
            <NewsCardSkeleton variant="card" />
          </div>
        ))}
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="bg-red-50 dark:bg-red-900/20 rounded-full p-8 mb-6">
          <svg className="w-24 h-24 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Failed to load feed</h3>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-4">
          We couldn't load this curated feed. The link may be expired or invalid.
        </p>
        <Button onClick={() => navigate('/dashboard/curation')} variant="default">
          Back to Curation
        </Button>
      </div>
    )
  }

  // Empty state
  if (articles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full p-8 mb-6">
          <svg className="w-24 h-24 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">No Articles Available</h3>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
          This curated feed has no articles available right now.
        </p>
        <Button onClick={() => navigate('/dashboard/curation')} variant="default">
          Back to Curation
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {articles.map((article, index) => (
        <NewsCard
          key={article?.alias || article?.id || `article-${index}`}
          article={article}
          variant="card"
          index={index}
        />
      ))}

      {/* Loading next page skeletons */}
      {(isFetchingNextPage || isPending) && (
        <>
          {[...Array(3)].map((_, i) => {
            const heights = ['min-h-[290px]', 'min-h-[310px]', 'min-h-[300px]']
            return (
              <div key={`skeleton-${i}`} className={heights[i]}>
                <NewsCardSkeleton variant="card" />
              </div>
            )
          })}
        </>
      )}

      {/* Load more trigger */}
      <div ref={loadMoreRef} className="py-4">
        {!hasNextPage && articles.length > 0 && (
          <p className="text-sm text-gray-500 text-center">You've reached the end</p>
        )}
      </div>
    </div>
  )
}

export function CurationReadPage() {
  const navigate = useNavigate()
  const { encodedAlias } = useParams()
  const [viewMode] = useState(getViewMode())

  // Decode the user alias
  const userAlias = useMemo(() => {
    try {
      return atob(encodedAlias)
    } catch {
      return null
    }
  }, [encodedAlias])

  // Fetch curator info
  const { data: curatorData, isLoading: loadingCurator } = useQuery({
    queryKey: ['curatorInfo', userAlias],
    queryFn: async () => {
      if (!userAlias) return null
      // Get all curated users and find the matching one
      const response = await curationApi.getCuratedUsers()
      const users = response.data.curated_users || []
      return users.find(u => u.alias === userAlias)
    },
    enabled: !!userAlias,
  })

  // Invalid curation param
  if (!userAlias) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Invalid Feed Link</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">This curation link appears to be invalid.</p>
            <Button onClick={() => navigate('/dashboard/curation')}>
              Back to Curation
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const curatorName = curatorData?.name || 'Curated Feed'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      {/* Curation Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard/curation')}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 -ml-2"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                {curatorData?.profile_picture ? (
                  <img
                    src={curatorData.profile_picture}
                    alt={curatorName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-bold">{curatorName}'s Feed</h1>
                  <p className="text-sm text-white/80">Curated News Feed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feed Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loadingCurator ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <CurationFeed curationParam={encodedAlias} curatorName={curatorName} />
        )}
      </div>
    </div>
  )
}
